import {AXE_RUN_OPTIONS} from './axeConfig'
import {mapAxeViolations, AxeIssue, AxeViolation} from './mapAxeResults'

export interface AxeApi {
  run: (context: Element, options: typeof AXE_RUN_OPTIONS) => Promise<{violations: AxeViolation[]}>
}

export interface RunAxeDeps {
  // Resolve an axe instance that lives in the SAME realm/window as the node being scanned.
  ensureAxe?: (win: Window & {axe?: AxeApi}) => Promise<AxeApi>
}

// axe-core must run INSIDE the realm of the node it scans. The CMS editor body lives in a
// same-origin TinyMCE iframe (a separate JS realm), and the parent's axe rejects an iframe
// node with "axe.run arguments are invalid" (its `instanceof Node` context check fails across
// realms). So we load axe-core INTO the iframe via a <script> tag — the same pattern the editor
// uses to load tinymce.min.js — and use that window's own axe instance. axe-core is vendored at
// public/axe.min.js (axe.source is not available in the npm build, so inline injection isn't an option).
const AXE_SRC = (process.env.PUBLIC_URL || '') + '/axe.min.js'

function defaultEnsureAxe(win: Window & {axe?: AxeApi}): Promise<AxeApi> {
  if (win.axe) return Promise.resolve(win.axe)
  return new Promise<AxeApi>((resolve, reject) => {
    const doc = win.document
    const script = doc.createElement('script')
    script.src = AXE_SRC
    script.onload = () => {
      if (win.axe) resolve(win.axe)
      else reject(new Error('axe-core loaded but did not initialize'))
    }
    script.onerror = () => reject(new Error('failed to load axe-core from ' + AXE_SRC))
    ;(doc.head || doc.documentElement).appendChild(script)
  })
}

export async function runAxeScan(node: Element, deps: RunAxeDeps = {}): Promise<AxeIssue[]> {
  const win = node.ownerDocument && (node.ownerDocument.defaultView as (Window & {axe?: AxeApi}) | null)
  if (!win) throw new Error('runAxeScan: node is not attached to a window')
  const axe = await (deps.ensureAxe ?? defaultEnsureAxe)(win)
  const results = await axe.run(node, AXE_RUN_OPTIONS)
  return mapAxeViolations(results.violations || [])
}